import { useQuery } from '@tanstack/react-query';

import { getUserSettingsQueryKey } from '@/consts/user-settings';
import { useFetch } from '@/hooks/useFetch';
import type { GetUserSettingsResponse, UserSettingsIdentifier } from '@/types/userSettingsApiTypes';

import { getUserSettingsUrl } from './utils/getUserSettingsUrl';

type UserSettingsResponseData = GetUserSettingsResponse['data'];

export type UseGetUserSettingsQueryOptions<TData = UserSettingsResponseData['settings']> = {
  identifier?: UserSettingsIdentifier;
  enabled?: boolean;
  staleTime?: number;
  suppressErrorToast?: boolean;
  mapData?: (data: unknown) => TData;
};

export function useGetUserSettingsQuery<TData = UserSettingsResponseData['settings']>({
  identifier,
  enabled = true,
  staleTime,
  suppressErrorToast = false,
  mapData,
}: UseGetUserSettingsQueryOptions<TData> = {}) {
  const { doFetch } = useFetch<void, GetUserSettingsResponse>();

  return useQuery({
    queryKey: getUserSettingsQueryKey(identifier),
    queryFn: async (): Promise<TData> => {
      const mapResponse = (payload: unknown): TData => {
        if (mapData) {
          return mapData(payload);
        }

        if (payload && typeof payload === 'object' && 'settings' in payload) {
          return (payload as UserSettingsResponseData).settings as TData;
        }

        return payload as TData;
      };

      try {
        const response = await doFetch(getUserSettingsUrl({ identifier }), {
          method: 'GET',
          suppressErrorToast,
        });

        return mapResponse(response.data);
      } catch {
        return mapResponse(null);
      }
    },
    enabled,
    staleTime,
  });
}
